"use client";

import Image from "next/image";
import { cn } from "@/shared/core/utils";
import { Button } from "@/shared/presentation/components/ui";
import AppImages from "@/constants/App_images";
import { clearAllSessionData } from "@/features/auth/domain/utils/clearSession";

type OAuthProvider = "john-deere" | "agworld";

interface OAuthButtonProps {
  provider: OAuthProvider;
  href: string;
  text: string;
  onClick?: () => void;
  disabled?: boolean;
  className?: string;
  variant?: "login" | "signup";
}

const providerConfig: Record<
  OAuthProvider,
  { logo: string; alt: string; bgClass: string; hoverClass: string }
> = {
  "john-deere": {
    logo: AppImages.kJohnDeereLogo,
    alt: "John Deere Logo",
    bgClass: "bg-john-deere-green",
    hoverClass: "hover:bg-john-deere-green/90",
  },
  agworld: {
    logo: AppImages.kAgworldLogo,
    alt: "AgWorld Logo",
    bgClass: "bg-agworld-black",
    hoverClass: "hover:bg-agworld-black/90",
  },
};

/**
 * OAuth button for third-party authentication providers
 * Supports John Deere and AgWorld OAuth flows
 *
 * IMPORTANT: Clears all session data before redirecting to the OAuth URL.
 * This prevents "session pollution" where a previous user's org/token data
 * survives the redirect round-trip and contaminates the new session.
 */
export function OAuthButton({
  provider,
  href,
  text,
  onClick,
  disabled = false,
  className,
}: OAuthButtonProps) {
  const config = providerConfig[provider];

  const handleClick = () => {
    if (disabled) return;

    if (onClick) {
      onClick();
    }

    // ── CRITICAL: Nuke all previous session data before leaving ──────
    // Without this, cookies/localStorage from a prior user survive the
    // OAuth redirect round-trip, causing the dashboard to load with
    // stale org IDs and tokens.
    clearAllSessionData();

    // Navigate to OAuth URL (backend handles the OAuth flow)
    window.location.href = href;
  };

  return (
    <Button
      type="button"
      onClick={handleClick}
      disabled={disabled}
      className={cn(
        "flex h-11 w-full items-center justify-center gap-3 rounded-md border-0 text-white",
        config.bgClass,
        config.hoverClass,
        "disabled:opacity-50 disabled:cursor-not-allowed",
        className
      )}
    >
      <Image
        src={config.logo}
        alt={config.alt}
        width={20}
        height={20}
        className="h-5 w-5"
      />
      <span>{text}</span>
    </Button>
  );
}

export default OAuthButton;
